import { Box, Stack, Typography } from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslate } from "src/locales";
import {
  PATIENT_AI_ANALYSIS_LOADING_STEP_IDS,
  PATIENT_AI_ANALYSIS_STEP_MS,
} from "./patientAiAnalysisLoadingSteps";

const floatGlow = keyframes`
  0% { transform: translate3d(0, -8px, 0) scale(1); opacity: 0.7; }
  50% { transform: translate3d(-8px, 10px, 0) scale(1.08); opacity: 1; }
  100% { transform: translate3d(6px, -2px, 0) scale(0.96); opacity: 0.75; }
`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const STEPS = PATIENT_AI_ANALYSIS_LOADING_STEP_IDS;
const RUSH_STEP_MS = 72;

type PatientAiAnalysisLoadingProps = {
  /** İlk yanıt metni geldiğinde kalan adımlar hızlıca tamamlanır. */
  rushToEnd?: boolean;
  onRushComplete?: () => void;
};

export default function PatientAiAnalysisLoading({
  rushToEnd = false,
  onRushComplete,
}: Readonly<PatientAiAnalysisLoadingProps>) {
  const { t } = useTranslate();
  const [elapsedMs, setElapsedMs] = useState(0);
  const [rushStep, setRushStep] = useState<number | null>(null);
  const elapsedMsRef = useRef(0);
  const onRushCompleteRef = useRef(onRushComplete);
  onRushCompleteRef.current = onRushComplete;
  elapsedMsRef.current = elapsedMs;

  useEffect(() => {
    const t0 = performance.now();
    const id = window.setInterval(() => {
      const ms = Math.round(performance.now() - t0);
      elapsedMsRef.current = ms;
      setElapsedMs(ms);
    }, 400);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!rushToEnd) {
      setRushStep(null);
      return;
    }

    const from = Math.min(STEPS.length - 1, Math.floor(elapsedMsRef.current / PATIENT_AI_ANALYSIS_STEP_MS));
    if (from >= STEPS.length - 1) {
      setRushStep(STEPS.length - 1);
      const done = window.setTimeout(() => onRushCompleteRef.current?.(), 100);
      return () => window.clearTimeout(done);
    }

    let i = from;
    setRushStep(i);
    const id = window.setInterval(() => {
      i += 1;
      const last = STEPS.length - 1;
      if (i >= last) {
        setRushStep(last);
        window.clearInterval(id);
        window.setTimeout(() => onRushCompleteRef.current?.(), 100);
        return;
      }
      setRushStep(i);
    }, RUSH_STEP_MS);

    return () => window.clearInterval(id);
  }, [rushToEnd]);

  const { stepIndex, label } = useMemo(() => {
    const normalIdx = Math.min(STEPS.length - 1, Math.floor(elapsedMs / PATIENT_AI_ANALYSIS_STEP_MS));
    const idx = rushStep !== null ? rushStep : normalIdx;
    const stepId = STEPS[idx];
    return {
      stepIndex: idx,
      label: t(`patient_ai_analysis.loading_steps.${stepId}`),
    };
  }, [elapsedMs, rushStep, t]);

  return (
    <Stack
      spacing={0}
      sx={{
        height: "100%",
        pt: 3,
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          position: "relative",
          minHeight: 520,
          borderRadius: 5,
          overflow: "hidden",
          background:
            "radial-gradient(circle at 28% 74%, rgba(255, 189, 76, 0.48) 0%, rgba(255, 189, 76, 0.08) 20%, transparent 42%), radial-gradient(circle at 72% 28%, rgba(255, 198, 114, 0.4) 0%, rgba(255, 198, 114, 0.08) 24%, transparent 44%), linear-gradient(180deg, #fffdfa 0%, #fff8ee 52%, #ffffff 100%)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: "20% 12% auto auto",
            width: 180,
            height: 180,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255, 187, 71, 0.95) 0%, rgba(255, 187, 71, 0.22) 30%, transparent 68%)",
            filter: "blur(22px)",
            animation: `${floatGlow} 4.8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            left: -36,
            right: 66,
            bottom: -22,
            height: 320,
            borderRadius: "58% 42% 0 0",
            transform: "rotate(-20deg)",
            background:
              "linear-gradient(90deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.78) 30%, rgba(255, 214, 138, 0.58) 52%, rgba(255,255,255,0.18) 100%)",
            backgroundSize: "200% 100%",
            animation: `${shimmer} 2.8s linear infinite`,
            boxShadow: `0 24px 60px ${alpha("#ffcc80", 0.24)}`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: 148,
            height: 148,
            transform: "translate(-50%, -50%)",
            borderRadius: "50%",
            border: "1px solid rgba(255, 208, 141, 0.42)",
            background: "radial-gradient(circle, rgba(255,255,255,0.96) 0%, rgba(255, 251, 243, 0.88) 64%, rgba(255,255,255,0) 100%)",
            boxShadow: `0 18px 44px ${alpha("#f6b24c", 0.18)}`,
          }}
        />
        <Stack
          alignItems="center"
          spacing={1.25}
          sx={{
            position: "absolute",
            zIndex: 1,
            inset: "0",
            px: 3,
            textAlign: "center",
            justifyContent: "flex-start",
            pt: 7,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "18px",
              background: "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,247,231,0.98) 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 14px 30px ${alpha("#f0a119", 0.16)}`,
              mb: 1,
            }}
          >
            <Box
              component="img"
              src="/assets/icons/nurse/ai-nurse-icon.png"
              alt="ai-analysis-loading"
              sx={{
                width: 28,
                height: 28,
                objectFit: "contain",
                animation: `${floatGlow} 3.2s ease-in-out infinite`,
              }}
            />
          </Box>
          <Typography sx={{ fontSize: 28, fontWeight: 700, color: "#ef8f00" }}>
            {t("patient_ai_analysis.loading_title")}
          </Typography>
          <Typography
            sx={{
              color: "#5c4a2e",
              fontSize: 14,
              fontWeight: 600,
              lineHeight: 1.45,
              minHeight: 44,
              px: 1,
              transition: "opacity 0.25s ease",
            }}
            key={STEPS[stepIndex]}
          >
            {label}
          </Typography>
          <Box
            sx={{
              mt: 2,
              width: 164,
              height: 6,
              borderRadius: 999,
              backgroundColor: "rgba(240, 161, 25, 0.12)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: "42%",
                height: "100%",
                borderRadius: 999,
                background: "linear-gradient(90deg, #f5b13d 0%, #ffcc80 100%)",
                animation: `${shimmer} 1.6s linear infinite`,
                backgroundSize: "200% 100%",
              }}
            />
          </Box>

          <Stack spacing={1.5} sx={{ width: "100%", mt: 5 }}>
            <Box
              sx={{
                width: 112,
                height: 12,
                borderRadius: 999,
                background: "linear-gradient(90deg, rgba(218,225,236,0.55) 0%, rgba(255,255,255,0.96) 50%, rgba(218,225,236,0.55) 100%)",
                backgroundSize: "200% 100%",
                animation: `${shimmer} 1.8s linear infinite`,
              }}
            />

            <Box
              sx={{
                width: "100%",
                borderRadius: 4,
                border: "1px solid rgba(224, 232, 243, 0.95)",
                backgroundColor: "rgba(248, 251, 255, 0.88)",
                boxShadow: "0 16px 34px rgba(15, 23, 42, 0.05)",
                p: 2,
              }}
            >
              <Stack spacing={1.2}>
                {[78, 96, 88, 92, 74, 84].map((width, index) => (
                  <Box
                    key={`${width}-${index}`}
                    sx={{
                      width: `${width}%`,
                      height: 11,
                      borderRadius: 999,
                      background: "linear-gradient(90deg, rgba(221,228,238,0.7) 0%, rgba(255,255,255,0.98) 50%, rgba(221,228,238,0.7) 100%)",
                      backgroundSize: "200% 100%",
                      animation: `${shimmer} ${1.7 + index * 0.15}s linear infinite`,
                    }}
                  />
                ))}
              </Stack>
            </Box>

            <Box
              sx={{
                width: "100%",
                borderRadius: 4,
                border: "1px solid rgba(240, 223, 195, 0.9)",
                background: "linear-gradient(180deg, rgba(255,250,242,0.94) 0%, rgba(255,255,255,0.92) 100%)",
                p: 2,
              }}
            >
              <Stack spacing={1}>
                {[64, 82, 76].map((width, index) => (
                  <Box
                    key={`footer-${width}-${index}`}
                    sx={{
                      width: `${width}%`,
                      height: 10,
                      borderRadius: 999,
                      background: "linear-gradient(90deg, rgba(245,214,162,0.52) 0%, rgba(255,255,255,0.96) 50%, rgba(245,214,162,0.52) 100%)",
                      backgroundSize: "200% 100%",
                      animation: `${shimmer} ${1.9 + index * 0.18}s linear infinite`,
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
}
